import { useState, type DragEvent } from 'react';

export interface DashboardCardConfig<TId extends string = string> {
  id: TId;
  title: string;
}

interface DashboardLayoutControlsProps<TId extends string> {
  cards: DashboardCardConfig<TId>[];
  order: TId[];
  hidden: Set<TId>;
  onMove: (id: TId, direction: -1 | 1) => void;
  onToggle: (id: TId) => void;
  onReset: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

/** Plain HTML customize panel — DX Sortable/CheckBox/Button can hit React #185 under React 19. */
export function DashboardLayoutControls<TId extends string>({
  cards,
  order,
  hidden,
  onMove,
  onToggle,
  onReset,
  onReorder
}: DashboardLayoutControlsProps<TId>) {
  const [collapsed, setCollapsed] = useState(false);
  const [dragFrom, setDragFrom] = useState<number | null>(null);

  function handleDragStart(index: number, e: DragEvent<HTMLButtonElement>) {
    setDragFrom(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }

  function handleDrop(toIndex: number, e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const fromIndex = dragFrom ?? Number.parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDragFrom(null);
    if (!Number.isFinite(fromIndex) || fromIndex === toIndex) return;
    onReorder(fromIndex, toIndex);
  }

  return (
    <div
      className={`dashboard-layout-controls${collapsed ? ' dashboard-layout-controls--collapsed' : ''}`}
    >
      <div className="dashboard-layout-controls__header">
        <div>
          <strong>Customize layout</strong>
          {!collapsed ? (
            <p className="dashboard-layout-controls__hint">
              Drag cards in the list or use the arrows to reorder. Uncheck a card to hide it.
              Use the width buttons on each card to resize it.
            </p>
          ) : null}
        </div>
        <div className="dashboard-layout-controls__header-actions">
          <button
            type="button"
            className="dashboard-actions__btn"
            title={collapsed ? 'Expand panel' : 'Collapse panel'}
            aria-label={collapsed ? 'Expand customize panel' : 'Collapse customize panel'}
            onClick={() => setCollapsed((value) => !value)}
          >
            <i className={`dx-icon dx-icon-${collapsed ? 'chevrondown' : 'chevronup'}`} aria-hidden />
          </button>
          <button
            type="button"
            className="dashboard-layout-controls__text-btn"
            title="Restore default card order, visibility, and widths"
            aria-label="Reset dashboard layout"
            onClick={onReset}
          >
            Reset
          </button>
        </div>
      </div>
      {collapsed ? null : (
        <div className="dashboard-layout-controls__list">
          {order.map((id, index) => {
            const card = cards.find((x) => x.id === id);
            if (!card) return null;
            return (
              <div
                key={id}
                className="dashboard-layout-controls__row"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(index, e)}
              >
                <button
                  type="button"
                  className="dashboard-layout-controls__drag-handle"
                  aria-label={`Drag ${card.title}`}
                  draggable
                  onDragStart={(e) => handleDragStart(index, e)}
                  onDragEnd={() => setDragFrom(null)}
                >
                  <span className="dx-icon-dragvertical" aria-hidden />
                </button>
                <label className="dashboard-layout-controls__check">
                  <input
                    type="checkbox"
                    checked={!hidden.has(id)}
                    onChange={() => onToggle(id)}
                  />
                  <span>{card.title}</span>
                </label>
                <div className="dashboard-layout-controls__move-buttons">
                  <button
                    type="button"
                    className="dashboard-actions__btn"
                    title={`Move ${card.title} up`}
                    aria-label={`Move ${card.title} up`}
                    disabled={index === 0}
                    onClick={() => onMove(id, -1)}
                  >
                    <i className="dx-icon dx-icon-chevronup" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="dashboard-actions__btn"
                    title={`Move ${card.title} down`}
                    aria-label={`Move ${card.title} down`}
                    disabled={index === order.length - 1}
                    onClick={() => onMove(id, 1)}
                  >
                    <i className="dx-icon dx-icon-chevrondown" aria-hidden />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
